/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {areEqual} from 'react-window';
import {translate} from '@ion/core/src/locale/locale';
import {FloatingIconButtonGroup} from '@ion/dls/src/components/buttons/FloatingIconButton';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {ExitIcon} from '@ion/dls/src/icons/ExitIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {IconSize, MarkerProps} from '@ion/dls/src/types/types';

type ExitToolProp = Omit<MarkerProps, 'color'>;

interface ExitToolProps {
  onExit: Function;
  show: boolean;
};

/**
 * The ExitToolComponent provides the UI for Exit Tool icon that can be placed on the
 * map layers.
 *
 * ```typescript
 *
 *   <ExitToolComponent size={} onClick={function() {}} />
 *
 * ```
 */
export const ExitToolComponent = ({size, onClick}: ExitToolProp) => (
  <FloatingIconButtonGroup>
    <FloatingIconButtonGroup.FloatingIconButton shimmer onClick={onClick}>
      <ExitIcon size={size} color={dlsColor('caBlue')} />
    </FloatingIconButtonGroup.FloatingIconButton>
  </FloatingIconButtonGroup>
);

/**
 * The ExitTool component is responsible for creating a Exit Tool/widget
 * component that provide an option to move the map to current location. This is a stand-alone
 * tool, can be usable in any of position on the map.
 *
 * ```typescript
 *
 *  import ExitTool from '../tools/ExitTool';
 *
 *  <ExitTool view={view} map={map} .. />
 *
 * ```
 */
export const ExitTool = ({
  onExit,
  show
}: ExitToolProps) => {
  const exit = useCallback(() => {
    onExit?.();
  }, []);
  
  if (show) {
    return (
      <Tooltip title={translate('maps:controls.close')}>
        <div>
          <ExitToolComponent size={IconSize.medium} onClick={exit} />
        </div>
      </Tooltip>
    );
  }
  return null;
};

export default memo(ExitTool, areEqual);
